"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import {
  PesquisasApiService,
  PesquisaAuthResponse,
} from "../../lib/services/pesquisasApi";

interface SurveyUser {
  id: string;
  nome: string;
  cpf: string;
  empresa: string;
}

interface LoginComponentProps {
  uuid: string;
}

export default function LoginComponent({ uuid }: LoginComponentProps) {
  const [token, setToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [isFinished, setIsFinished] = useState(false);
  const [blockingError, setBlockingError] = useState(false); // Erro que impede login
  const router = useRouter();

  useEffect(() => {
    if (!uuid) {
      setError("UUID da pesquisa não encontrado na URL");
      setBlockingError(true);
      setCheckingStatus(false);
      return;
    }

    // Verificar status da pesquisa antes de permitir login
    const checkPesquisaStatus = async () => {
      try {
        const status = await PesquisasApiService.checkStatus(uuid);

        // Verificar se a pesquisa já foi finalizada
        if (status.is_finished || status.pesquisa_empresa?.is_finished) {
          setIsFinished(true);
          setError(
            "Esta pesquisa já foi finalizada. O link não é mais válido.",
          );
          setBlockingError(true);
        } else if (!status.enabled) {
          setError("Acesso negado: colaborador desabilitado na pesquisa.");
          setBlockingError(true);
        } else if (!status.pesquisa_empresa?.enabled) {
          setError("Acesso negado: pesquisa da empresa desabilitada.");
          setBlockingError(true);
        } else if (!status.pesquisa_empresa?.is_vigente) {
          setError("Período da pesquisa encerrado.");
          setBlockingError(true);
        }
      } catch (err: any) {
        console.error("Erro ao verificar status da pesquisa:", err);
        if (err.response?.status === 404) {
          setError(
            "Pesquisa não encontrada. Verifique se o link está correto.",
          );
          setBlockingError(true);
        } else {
          // Não mostrar erro genérico, apenas logar
          console.error("Erro ao verificar status:", err);
        }
      } finally {
        setCheckingStatus(false);
      }
    };

    checkPesquisaStatus();
  }, [uuid]);

  const handleLogin = async () => {
    setLoading(true);
    setError("");
    setBlockingError(false);

    try {
      // Autenticar via API
      const authResponse: PesquisaAuthResponse =
        await PesquisasApiService.authenticate({
          uuid: uuid,
          secret: token,
        });

      // Limpar dados antigos se o UUID mudou (apenas dados de autenticação)
      const oldSurveyId = localStorage.getItem("surveyId");
      if (oldSurveyId && oldSurveyId !== uuid) {
        // Limpar dados de autenticação antigos
        localStorage.removeItem("surveyUser");
      }

      // Salvar token JWT
      PesquisasApiService.setToken(authResponse.access_token);

      // Salvar dados do colaborador
      const userData: SurveyUser = {
        id: (
          authResponse.pesquisa_colaborador.colaborador_id ||
          authResponse.pesquisa_colaborador.id
        ).toString(),
        nome: authResponse.pesquisa_colaborador.colaborador.nome,
        cpf: "", // Não retornado pela API por segurança
        empresa: "Empresa", // Não retornado diretamente, mas poderia ser obtido
      };

      localStorage.setItem("surveyUser", JSON.stringify(userData));
      localStorage.setItem("surveyId", uuid);
      // Não salvar a senha (survey_secret) por segurança

      // Aguardar um pouco para garantir que o localStorage foi salvo
      // await new Promise(resolve => setTimeout(resolve, 200));

      // Redirecionar para coleta de dados antes da pesquisa
      // Usar router.push para navegação client-side (mais rápida e mantém estado se necessário)
      router.push("/dados-colaborador");
    } catch (err: any) {
      console.error("Erro na autenticação:", err);
      setError(
        err.response?.data?.message ||
        err.message ||
        "Erro ao acessar a pesquisa. Verifique suas credenciais.",
      );
      // Erro de autenticação não bloqueia tentativas futuras
      setBlockingError(false);
    } finally {
      setLoading(false);
    }
  };

  const handleTokenChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newToken = e.target.value;
    setToken(newToken);
    // Limpar erro quando usuário começa a digitar novamente (exceto erros bloqueantes)
    if (error && !blockingError) {
      setError("");
    }
    // Garantir que blockingError está false quando usuário digita (exceto se for erro bloqueante real)
    if (blockingError && !isFinished) {
      // Se não é erro de pesquisa finalizada, pode ser que seja erro temporário
      // Permitir que usuário tente novamente
      setBlockingError(false);
    }
  };

  if (!uuid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Erro</h1>
            <p className="text-gray-600">
              UUID da pesquisa não encontrado na URL.
            </p>
            <p className="text-sm text-gray-500 mt-2">
              Use uma URL válida como: /login/uuid-da-pesquisa
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Mostrar loading enquanto verifica status
  if (checkingStatus) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Verificando status da pesquisa...</p>
          </div>
        </div>
      </div>
    );
  }

  // Se a pesquisa já foi finalizada, mostrar mensagem e não permitir login
  if (isFinished) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
          <div className="text-center mb-8">
            <div className="text-6xl mb-4">✅</div>
            <h1 className="text-3xl font-bold text-green-600 mb-2">
              SST Exames
            </h1>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Pesquisa Psicossocial
            </h2>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-lg mb-6">
            <p className="font-medium mb-2">⚠️ Pesquisa Já Finalizada</p>
            <p className="text-sm">
              Esta pesquisa já foi respondida e finalizada. O link não é mais
              válido.
            </p>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
            <h3 className="font-medium text-gray-900 mb-2">Detalhes</h3>
            <div className="text-sm text-gray-600 space-y-1">
              <p>
                <strong>UUID:</strong> {uuid}
              </p>
              <p>
                <strong>Status:</strong> Finalizada
              </p>
            </div>
          </div>

          <div className="text-center text-sm text-gray-500">
            <p>
              Se você acredita que isso é um erro, entre em contato com o RH da
              sua empresa.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const isTokenValid = token.trim().length > 0;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-green-600 mb-2">SST Exames</h1>
          <h2 className="text-xl font-semibold text-gray-900">
            Pesquisa Psicossocial
          </h2>
          <p className="text-gray-600 mt-2">
            Digite o token de acesso para continuar
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        <div className="space-y-4 mb-6">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h3 className="font-medium text-gray-900 mb-2">
              Detalhes da Pesquisa
            </h3>
            <div className="text-sm text-gray-600 space-y-1">
              <p>
                <strong>UUID:</strong> {uuid}
              </p>
              <p>
                <strong>Tipo:</strong> Pesquisa Psicossocial
              </p>
            </div>
          </div>

          <div>
            <label
              htmlFor="token"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Token de Acesso
            </label>
            <input
              id="token"
              type="text"
              value={token}
              onChange={handleTokenChange}
              onKeyPress={(e) => {
                if (
                  e.key === "Enter" &&
                  isTokenValid &&
                  !loading &&
                  !blockingError
                ) {
                  handleLogin();
                }
              }}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white text-gray-900"
              placeholder="Digite o token de acesso"
              autoComplete="off"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck="false"
              required
              disabled={blockingError}
            />
          </div>
        </div>

        <button
          onClick={handleLogin}
          disabled={loading || !isTokenValid || blockingError}
          className={`w-full py-3 px-4 rounded-lg text-white font-semibold transition-colors ${loading || !isTokenValid || blockingError
            ? "bg-gray-400 cursor-not-allowed"
            : "bg-green-600 hover:bg-green-500"
            }`}
        >
          {loading ? "Carregando..." : "Iniciar Pesquisa"}
        </button>

        <div className="mt-6 text-center text-sm text-gray-500">
          <p>Esta pesquisa é confidencial e seus dados estão protegidos.</p>
        </div>
      </div>
    </div>
  );
}
